import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Project } from './api';
import { useProjectTree } from './project-tree';

/**
 * Resolves the current project from the `:projectSlug` route param.
 * Every project-scoped page (Ingest, Review, Memory, Feed, Search)
 * lives under `/p/:projectSlug/...`, so this is the single source of truth
 * for "which project am I looking at" instead of each page keeping its own
 * project dropdown/state.
 *
 * Reads the shared hierarchy rather than fetching its own list: this hook and the sidebar used
 * to request the project list separately, so every KB page fetched it twice.
 */
export function useCurrentProject() {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const navigate = useNavigate();
  const { allProjects, organization, loading } = useProjectTree();

  const project = (allProjects.find((p) => p.slug === projectSlug) as Project | undefined) ?? null;

  // An unknown slug lands on the organization — the root of the tree, and the only project
  // guaranteed to exist. There is no all-projects page to fall back to any more.
  useEffect(() => {
    if (!loading && projectSlug && allProjects.length > 0 && !project && organization) {
      navigate(`/p/${organization.slug}`, { replace: true });
    }
  }, [loading, projectSlug, allProjects, project, organization, navigate]);

  return { project, projects: allProjects as Project[], loading, projectSlug: projectSlug ?? '' };
}
